import React, { useContext, useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate, useParams } from "react-router";
import axios from "axios";
import { Apis } from "../util/Apis";
import ContainerBackgroundWrapper from "../components/Backgrounds";
import { tourDuration } from "../components/hotseat/hotseatMotion";
import { SiteContext } from "../context/SiteContext";
import CasinoFireballScene from "../components/hotseat/casino2d/CasinoFireballScene";
import HotSeatLoading from "../components/hotseat/HotSeatLoading";
import Button from "../components/atoms/Buttons";

const LEAVE_MS = 1100;
const dots = keyframes`
  0%, 20% { opacity: 0; }
  40%, 100% { opacity: 1; }
`;
const Stage = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background: #0b0709;
  color: #fff0d7;
  overflow: hidden;
  opacity: 1;
  transition: opacity ${LEAVE_MS}ms cubic-bezier(0.45, 0, 0.55, 1);
  &[data-leaving="true"] { opacity: 0; }
`;
const Heading = styled.header`
  position: absolute;
  top: 0;
  inset-inline: 0;
  padding: clamp(16px, 3vh, 32px) 20px 38px;
  text-align: center;
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(#100e15e8, #100e1599 65%, transparent);
  transition: opacity 600ms ease;
  &[data-hidden="true"] { opacity: 0; }
  .dots i {
    font-style: normal;
    animation: ${dots} 1.4s infinite both;
  }
  .dots i:nth-child(2) { animation-delay: 0.2s; }
  .dots i:nth-child(3) { animation-delay: 0.4s; }
  @media (prefers-reduced-motion: reduce) { .dots i { animation: none; } }
  span {
    color: #ebc688;
    letter-spacing: 4px;
    font-size: 12px;
  }
  h1 {
    margin: 8px 0 0;
    font-size: clamp(24px, 3.6vw, 52px);
    font-weight: 800;
    letter-spacing: -0.03em;
    color: #ffe9a4;
    text-shadow: 0 1px #ffd875, 0 3px #b8791a, 0 5px #72300d, 0 8px 14px #280000;
    overflow-wrap: anywhere;
  }
`;
const Message = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  padding: 80px 24px 24px;
  text-align: center;
  background: radial-gradient(ellipse, #331820, #100e15);
  p {
    max-width: 580px;
    line-height: 1.6;
  }
`;

const prefersReducedMotion = () =>
  typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Misma lógica de sorteo que PlayHotSeat (una sola generación por montaje, el servidor decide el
// ganador), pero con la sala en canvas 2D. Al terminar navega a la pantalla de ganador existente.
function Draw({ id, salaId }) {
  const navigate = useNavigate();
  const generation = useRef(null);
  const [startedAt] = useState(() => Date.now());
  const navigated = useRef(false);
  const [attempt, setAttempt] = useState(0);
  const [outcome, setOutcome] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [error, setError] = useState(null);
  const [fallback, setFallback] = useState(prefersReducedMotion);
  const [sceneReady, setSceneReady] = useState(false);
  const [phase, setPhase] = useState("tour");
  const [leaving, setLeaving] = useState(false);
  const leaveTimer = useRef(null);
  const durationMs = Math.min(8000, tourDuration(salaId));
  const url = `${Apis.SORTEOS}/sorteos/${id}`;

  useEffect(() => {
    let active = true;
    if (!generation.current) {
      const job = { confirmed: false, promise: null };
      generation.current = job;
      job.promise = axios
        .post(`${url}/generate-winner-hot-seat`, null, { timeout: 60000 })
        .then(() => {
          job.confirmed = true;
          return axios.get(`${url}/winner`, { timeout: 20000 });
        });
    }
    generation.current.promise
      .then(({ data }) => {
        if (!active) return;
        setError(null);
        setOutcome({ winner: data && Number(data.confId) > 0 ? data : null });
      })
      .catch(() => {
        if (active) setError(generation.current.confirmed ? "result" : "generation");
      });
    return () => {
      active = false;
    };
  }, [url, attempt]);

  useEffect(() => {
    const abort = new AbortController();
    axios
      .get(`${url}/participants`, { signal: abort.signal, timeout: 15000 })
      .then(({ data }) => {
        if (!abort.signal.aborted && Array.isArray(data)) setParticipants(data);
      })
      .catch(() => {}); // Las etiquetas de máquina son opcionales; el ganador llega aparte.
    return () => abort.abort();
  }, [url]);

  useEffect(() => () => clearTimeout(leaveTimer.current), []);

  const goToWinner = (fadeIn = false) => {
    if (!outcome || error || navigated.current) return;
    navigated.current = true;
    navigate(`/winner-view-hotseat/${id}`, { state: { hotSeatReveal: { id, winner: outcome.winner, fadeIn } } });
  };

  // Al terminar el acercamiento la sala se funde a negro y recién entonces se cambia de vista;
  // la pantalla del ganador entra con su propio fundido (fadeIn).
  const complete = () => {
    if (!outcome || error || navigated.current || leaveTimer.current) return;
    setLeaving(true);
    leaveTimer.current = setTimeout(() => goToWinner(true), LEAVE_MS);
  };

  // Sin animación (movimiento reducido o fallo del canvas): se espera el tiempo mínimo y se navega.
  useEffect(() => {
    if (!fallback || !outcome || error) return;
    const timer = setTimeout(() => goToWinner(), Math.max(0, durationMs - (Date.now() - startedAt)));
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fallback, outcome, error, durationMs]);

  const readResult = () => {
    setError(null);
    setSceneReady(false);
    generation.current.promise = axios.get(`${url}/winner`, { timeout: 20000 });
    setAttempt((value) => value + 1);
  };

  const green = Number(salaId) === 332;
  return (
    <Stage data-leaving={leaving}>
      {(sceneReady || error || fallback) && (
        <Heading data-hidden={!error && !fallback && (leaving || phase === "zoom" || phase === "empty")}>
          <span>HOT SEAT</span>
          <h1 role="status" aria-live="polite">
            {error ? (
              "No se pudo consultar el resultado"
            ) : (
              <>
                Buscando la máquina ganadora
                <span className="dots" aria-hidden="true"><i>.</i><i>.</i><i>.</i></span>
              </>
            )}
          </h1>
        </Heading>
      )}
      {!error && !fallback && (
        <CasinoFireballScene
          outcome={outcome}
          participants={participants}
          durationMs={durationMs}
          onReady={() => setSceneReady(true)}
          onComplete={complete}
          onPhase={setPhase}
          onFallback={() => setFallback(true)}
        />
      )}
      {!error && !fallback && <HotSeatLoading ready={sceneReady} green={green} />}
      {!error && fallback && (
        <Message>
          <p>Esperando el resultado del sorteo…</p>
        </Message>
      )}
      {error && (
        <Message role="alert">
          <p>
            {error === "result"
              ? "El sorteo se realizó, pero no pudimos leer el resultado. Puedes consultarlo nuevamente."
              : "No pudimos confirmar la respuesta del sorteo. Revisa su estado antes de iniciar otro."}
          </p>
          {error === "result" && <Button onClick={readResult}>Consultar resultado</Button>}
          <Button variant="outline" onClick={() => navigate("/sorteo-list")}>Volver a sorteos</Button>
        </Message>
      )}
    </Stage>
  );
}

export default function PlayHotSeatCasino() {
  const { id } = useParams();
  const { salaId } = useContext(SiteContext);
  return (
    <ContainerBackgroundWrapper showBackgroundCard={false}>
      {salaId == null ? (
        <Stage>
          <HotSeatLoading />
        </Stage>
      ) : (
        <Draw key={`${salaId}:${id}`} id={id} salaId={salaId} />
      )}
    </ContainerBackgroundWrapper>
  );
}
